import type React from 'react';
import type { MutableRefObject } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import {
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
} from '@floating-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { TypeaheadMenuPluginProps } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootNode,
  COMMAND_PRIORITY_HIGH,
} from 'lexical';

import { $createActionNode } from 'components/molecules/LexicalEditor/features/action/nodes/ActionNode';
import { OPEN_ACTION_PICKER_COMMAND } from 'components/molecules/LexicalEditor/plugins/ActionPicker/commands';
import { CreateActionPopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/CreateActionPopup';
import {
  CreateActionContext,
  CreateActionProvider,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/CreateActionPopup/provider';
import {
  ActionMenu,
  MAX_HEIGHT as MENU_MAX_HEIGHT,
  Z_INDEX as MENU_Z_INDEX,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/Menu';
import { fuzzyScore } from 'components/molecules/LexicalEditor/plugins/ActionPicker/fuzzy';
import {
  RESOURCE_KEYS_MAPPING,
  ReservedActions,
  usePickerOptions,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/hooks/usePickerOptions';

// Maximum acceptable fuzzy score for filtering. Lower scores = better matches.
// Fuse.js scores are 0-100 (0 = perfect match, 100 = very poor match).
// This prevents very poor matches from appearing in the picker.
const MAX_FUZZY_SCORE = 40;

import type {
  ActionPickerConfig,
  PickerOption,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import { MenuOptions } from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import useTypeaheadTriggerMatch from './hooks/useTypeaheadTriggerMatch';

interface ActionPickerPluginProps {
  editorId: string;
  config?: ActionPickerConfig;
}

function ActionPicker({
  editorId,
  config,
}: ActionPickerPluginProps & {
  config: ActionPickerConfig;
}) {
  const [editor] = useLexicalComposerContext();
  const { setActivePopup, triggerPopup } = useContext(CreateActionContext);
  const groups = usePickerOptions({ config });
  const [shouldShowMenu, setShouldShowMenu] = useState(true);
  const [query, setQuery] = useState<string | null>(null);
  const [isOpenManually, setIsOpenManually] = useState(false);

  const floatingRef = useRef<HTMLElement | null>(null);
  const { floatingStyles, refs, update, context } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    open: isOpenManually,
    onOpenChange(open: boolean) {
      setIsOpenManually(open);
    },
  });

  const dismiss = useDismiss(context, {
    outsidePress: true,
    escapeKey: true,
  });

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnOpen: true,
    scrollItemIntoView: true,
  });

  const { getFloatingProps } = useInteractions([dismiss, listNavigation]);

  const [targetNodeKey, setTargetNodeKey] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    return editor.registerCommand(
      OPEN_ACTION_PICKER_COMMAND,
      payload => {
        refs.setReference(payload.anchorElement);
        setTargetNodeKey(payload.paragraphNodeKey);

        setIsOpenManually(true);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, refs]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  useEffect(() => {
    if (floatingRef.current) {
      refs.setFloating(floatingRef.current);
      update?.();
    }
  }, [floatingRef, refs, update]);

  const checkForTriggerMatch = useTypeaheadTriggerMatch('/', {
    minLength: 0,
  });

  const focusEditorOnNextTick = useCallback(() => {
    setTimeout(() => {
      const editorElement = editor.getRootElement();
      if (editorElement) {
        editorElement.focus();
      }
    }, 0);
  }, [editor]);

  const onCreateAction = useCallback(
    async ({
      placeholderKey,
      option,
      closeMenu,
    }: {
      placeholderKey: string;
      option: PickerOption;
      closeMenu?: () => void;
    }) => {
      try {
        const resolution = await new Promise<Record<string, any>>(
          (resolve, reject) => {
            triggerPopup(resolve, reject, option);
          },
        );

        editor.update(() => {
          const keys = RESOURCE_KEYS_MAPPING[option.menuOption] ?? {
            id: 'id',
            name: 'name',
          };

          const actionNode = $createActionNode({
            prefix: option.prefix,
            id: resolution[keys.id],
          });

          const placeholder = $getNodeByKey(placeholderKey);
          placeholder?.replace(actionNode);
          closeMenu?.();
        });

        setActivePopup();

        focusEditorOnNextTick();
      } catch (error) {
        // if the reason this was rejected is because a new popup was opened but the old one did not clean up
        // we do nothing!
        if (typeof error === 'string' && error === 'new_popup') {
          return;
        }

        editor.update(() => {
          const placeholder = $getNodeByKey(placeholderKey);

          if (placeholder) {
            placeholder?.remove();
            closeMenu?.();
          }
        });

        setActivePopup();

        focusEditorOnNextTick();
      }
    },
    [editor, setActivePopup, triggerPopup, focusEditorOnNextTick],
  );

  const onOptionSelect: TypeaheadMenuPluginProps<PickerOption>['onSelectOption'] =
    useCallback(
      (option, textNodeContainingQuery, closeMenu) => {
        if (option.type === 'option' && !option.setup) {
          if (option.transform) {
            try {
              const result = option.transform(option);
              const actionNode = $createActionNode({
                prefix: result.prefix || option.prefix,
                id: result.id,
              });
              textNodeContainingQuery?.replace(actionNode);
            } catch {
              // noop
            }
            closeMenu?.();

            return;
          }

          const actionNode = $createActionNode({
            prefix: option.prefix,
            id: option.id,
          });
          textNodeContainingQuery?.replace(actionNode);
          closeMenu();

          return;
        }

        if (option.type === 'option' && option.setup) {
          // using the action node as a placeholder due to some issue with replacing a regular text node.
          const placeholder = $createActionNode({ prefix: '', id: '' });
          textNodeContainingQuery?.replace(placeholder);

          setIsOpenManually(false);

          // since it's also just opening the popup, we can reuse
          void onCreateAction({
            placeholderKey: placeholder.getKey(),
            option,
            closeMenu,
          });
        }

        if (option.type === 'action') {
          // IMPORTANT:
          // because `onOptionSelect` is inside the closure of an `editor.update()`
          // the async call to each action handler will make all the nodes we have
          // here stale, thus rendering `textNodeContainingQuery` useless. We create a new empty node and
          // save the key so that it can be replaced instead with a proper node.
          const placeholder = $createActionNode({ prefix: '', id: '' });
          textNodeContainingQuery?.replace(placeholder);

          setIsOpenManually(false);

          switch (option.id) {
            case ReservedActions.CREATE_ACTION:
              void onCreateAction({
                placeholderKey: placeholder.getKey(),
                option,
              });
              break;

            default:
              break;
          }
        }
      },
      [onCreateAction],
    );

  const handleManualSelect = useCallback(
    (option: PickerOption) => {
      editor.update(() => {
        const paragraph = $getNodeByKey(targetNodeKey || $getRoot().getKey());
        if (!paragraph) {
          setIsOpenManually(false);
          return;
        }

        const placeholder = $createTextNode('/');

        const selection = $getSelection();
        if (selection && $isRangeSelection(selection)) {
          selection.insertNodes([placeholder]);
        } else {
          const root = $getRoot();
          const resolvedParagraph =
            !$isRootNode(paragraph) && $isElementNode(paragraph)
              ? paragraph
              : (() => {
                  const first = root.getFirstChild();
                  if (first && $isElementNode(first) && !$isRootNode(first)) {
                    return first;
                  }
                  const p = $createParagraphNode();
                  root.append(p);
                  return p;
                })();

          resolvedParagraph.append(placeholder);
        }

        onOptionSelect(option, placeholder, () => setIsOpenManually(false), '');
      });

      focusEditorOnNextTick();
    },
    [editor, onOptionSelect, targetNodeKey, focusEditorOnNextTick],
  );

  useEffect(() => {
    if (!isOpenManually && activeIndex && activeIndex > 0) {
      setActiveIndex(0);
      listRef.current = [];
    }
  }, [activeIndex, isOpenManually]);

  const options = useMemo(() => {
    return groups.flatMap(group => group.options ?? []);
  }, [groups]);

  const filtered = useMemo(() => {
    const base = !query
      ? options
      : options.filter(o => {
          const nameScore = fuzzyScore(query, o.name);
          if (nameScore !== Infinity && nameScore <= MAX_FUZZY_SCORE) {
            return true;
          }

          return o.keywords?.some(keyword => {
            const keywordScore = fuzzyScore(query, keyword);
            return keywordScore !== Infinity && keywordScore <= MAX_FUZZY_SCORE;
          });
        });

    return base.map((option, idx) => {
      option.index = idx;
      return option;
    });
  }, [query, options]);

  useEffect(() => {
    const handleClick = (event: Event) => {
      const target = event.target as Element;
      const isWithinEditor = target.closest(`[data-rti-id="${editorId}"]`);
      const isWithinMenu = target.closest(
        `[data-rti-id="${editorId}-action-menu"]`,
      );

      setShouldShowMenu(Boolean(isWithinEditor || isWithinMenu));
    };

    document.addEventListener('mousedown', handleClick, true);
    document.addEventListener('touchstart', handleClick, true);

    return () => {
      document.removeEventListener('mousedown', handleClick, true);
      document.removeEventListener('touchstart', handleClick, true);
    };
  }, [editorId]);

  if (isOpenManually) {
    return ReactDOM.createPortal(
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          zIndex: MENU_Z_INDEX,
        }}
        {...getFloatingProps({
          tabIndex: 0,
        })}
      >
        <ActionMenu
          dataId={`${editorId}-action-menu`}
          groups={groups}
          menuRenderProps={{
            options: options.map(option => {
              option.setRefElement = element => {
                listRef.current[option.index] = element;
              };
              return option;
            }),
            selectOptionAndCleanUp: handleManualSelect,
            selectedIndex: activeIndex,
            setHighlightedIndex: setActiveIndex,
          }}
        />
      </div>,
      document.body,
    );
  }

  const getMenuStyle = (
    anchorElementRef: MutableRefObject<HTMLElement | null>,
  ): React.CSSProperties | undefined => {
    if (!anchorElementRef.current) {
      return undefined;
    }

    const anchorRect = anchorElementRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - anchorRect.bottom;
    const shouldPlaceAbove =
      spaceBelow < MENU_MAX_HEIGHT && anchorRect.top > MENU_MAX_HEIGHT;

    return shouldPlaceAbove
      ? {
          position: 'absolute',
          bottom: 'calc(100% + 24px)',
          left: 0,
        }
      : undefined;
  };

  return (
    <LexicalTypeaheadMenuPlugin<PickerOption>
      menuRenderFn={(anchorElementRef, props) => {
        // this is actually a component
        // eslint-disable-next-line react-hooks/rules-of-hooks
        // biome-ignore lint/correctness/useHookAtTopLevel: existing
        useEffect(() => {
          return () => {
            setShouldShowMenu(true);
          };
        }, []);

        if (
          !anchorElementRef.current ||
          props.options.length === 0 ||
          !shouldShowMenu
        ) {
          return null;
        }

        return ReactDOM.createPortal(
          <ActionMenu
            key="manual"
            dataId={`${editorId}-action-menu`}
            groups={groups}
            menuRenderProps={props}
            style={getMenuStyle(anchorElementRef)}
          />,
          anchorElementRef.current,
        );
      }}
      options={filtered}
      triggerFn={checkForTriggerMatch}
      onQueryChange={setQuery}
      onSelectOption={onOptionSelect}
    />
  );
}

const DEFAULT_CONFIG: ActionPickerConfig = Object.values(MenuOptions).reduce(
  (acc, option) => ({
    ...acc,
    [option]: { select: true, create: true },
  }),
  {} as ActionPickerConfig,
);

export function ActionPickerPlugin({
  config = DEFAULT_CONFIG,
  editorId,
}: ActionPickerPluginProps) {
  return (
    <CreateActionProvider>
      <ActionPicker config={config} editorId={editorId} />
      <CreateActionPopup />
    </CreateActionProvider>
  );
}
